import React, { useState, useEffect, useRef } from 'react';
import { Info, AlertCircle, CheckCircle2, XCircle, X } from 'lucide-react';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

export interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  onClose?: () => void;
}

const typeConfig: Record<ToastType, { dot: string; iconColor: string; Icon: typeof Info }> = {
  success: { dot: 'bg-pb', iconColor: 'text-pb', Icon: CheckCircle2 },
  warning: { dot: 'bg-warn', iconColor: 'text-warn', Icon: AlertCircle },
  error: { dot: 'bg-bad', iconColor: 'text-bad', Icon: XCircle },
  info: { dot: 'bg-primary', iconColor: 'text-primary', Icon: Info },
};

export const Toast: React.FC<ToastProps> = ({
  message,
  type = 'info',
  duration = 5000,
  onClose,
}) => {
  const [state, setState] = useState<'entering' | 'visible' | 'exiting'>('entering');
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    // Trigger enter animation
    const enterTimer = requestAnimationFrame(() => setState('visible'));

    if (duration > 0) {
      timerRef.current = setTimeout(() => {
        setState('exiting');
      }, duration);
    }

    return () => {
      cancelAnimationFrame(enterTimer);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [duration]);

  const handleClose = () => {
    setState('exiting');
  };

  const handleTransitionEnd = () => {
    if (state === 'exiting' && onClose) {
      onClose();
    }
  };

  const { dot, iconColor, Icon } = typeConfig[type];

  return (
    <div
      className={`
        bg-card text-card-foreground border border-border shadow-lift px-4 py-3 rounded-full flex items-center justify-between
        transition-all duration-300 ease-out
        ${state === 'entering' ? 'translate-x-full opacity-0' : ''}
        ${state === 'visible' ? 'translate-x-0 opacity-100' : ''}
        ${state === 'exiting' ? 'translate-x-full opacity-0' : ''}
      `}
      role="alert"
      onTransitionEnd={handleTransitionEnd}
    >
      <div className="flex items-center">
        <span className={`h-2 w-2 rounded-full flex-shrink-0 ${dot}`} aria-hidden="true" />
        <Icon className={`w-5 h-5 flex-shrink-0 ml-2.5 ${iconColor}`} />
        <div className="ml-2.5 text-sm font-medium">{message}</div>
      </div>
      <button
        onClick={handleClose}
        className="ml-4 p-1 rounded-full hover:bg-secondary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="Dismiss notification"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default Toast;
